// ─────────────────────────────────────────────────────────────────────────────
// AUTHENTICATION — whichever backend is configured
//
// Core signed in through Supabase, which redirected to Microsoft and issued its
// own token. On Azure there is no Supabase, so Core talks to Entra directly.
//
// Rather than replace one with the other, this picks between them by which
// environment variables are set. Two consequences worth having:
//
//   The switch is a SETTING, not a deployment. Setting the three Entra values
//   moves authentication; removing them moves it back. A rollback at four in
//   the afternoon should not require a build.
//
//   Both paths stay tested. The Supabase route is what Core runs on today and
//   will keep running on until the switch is finished, so deleting it now would
//   mean the live system depended on code nobody had exercised since.
//
// The two files that import from here — the login page and the shell — are
// unchanged. They see the same six exports either way.
// ─────────────────────────────────────────────────────────────────────────────

import * as entra    from "./affinity_auth_entra";
import * as supa     from "./affinity_auth_supabase";
import { setTokenProvider, usingEntra } from "./affinity_accounting_supabase";

const impl = usingEntra ? entra : supa;

// Every database request asks this for a current token. Wired here because this
// is the module that knows which backend is in use — the client itself should
// not have to.
if (usingEntra) {
  setTokenProvider(async () => {
    const session = await entra.getSession();
    return session ? session.access_token : null;
  });
}

export const isAuthConfigured    = impl.isAuthConfigured;
export const signInWithMicrosoft = impl.signInWithMicrosoft;
export const signOut             = impl.signOut;
export const getSession          = impl.getSession;
export const onAuthChange        = impl.onAuthChange;

export { identityFromSession } from "./affinity_auth_identity";

// Only meaningful on Entra: whether this person has been assigned the app role.
// On Supabase everybody who can sign in has access, so it is true.
export const hasAccess = usingEntra
  ? entra.hasAccess
  : (session) => Boolean(session);

export const NO_ACCESS_MESSAGE = usingEntra
  ? entra.NO_ACCESS_MESSAGE
  : "Your account does not have access to Core.";

// Which backend is actually in use, for the sign-in screen to say so during the
// changeover. Somebody testing needs to know which one they just used.
export const authBackend = usingEntra ? "entra" : "supabase";
